/**
 * "Who sounds?" — timbre discrimination + vocabulary. Something sounds and the
 * child picks the right image among the options; the narrator names it in the
 * active language.
 *
 * The pool is data-driven: the MVP uses instruments and pads (existing
 * assets); animals come in later by changing only the pool.
 */

export interface WhoSoundsItem<Id extends string = string> {
  /** Stable id — it is also the narrator key (`narration/keys.ts`). */
  id: Id;
  /** Emoji or image path for the card. */
  icon: string;
  /** How it sounds: instrument + SAMPLE_MANIFEST key. */
  sound: { instrument: "piano" | "xylophone" | "drums"; key: string };
}

export interface WhoSoundsConfig<Id extends string = string> {
  pool: readonly WhoSoundsItem<Id>[];
  /** Options per round (including the correct one). */
  choices: number;
  rounds: number;
  random?: () => number;
}

export interface WhoSoundsRound<Id extends string = string> {
  target: WhoSoundsItem<Id>;
  options: WhoSoundsItem<Id>[];
}

export interface WhoSoundsState {
  round: number;
  /** First-try hits (progress metric). */
  firstTryHits: number;
  finished: boolean;
}

export interface WhoSoundsGame<Id extends string = string> {
  state: () => WhoSoundsState;
  currentRound: () => WhoSoundsRound<Id>;
  /**
   * The child's choice: "hit" advances the round (or "finished" if it was the last),
   * "miss" lets you retry the same round.
   */
  choose: (id: Id) => "hit" | "miss" | "finished";
}

export function createWhoSoundsGame<Id extends string = string>(
  config: WhoSoundsConfig<Id>,
): WhoSoundsGame<Id> {
  const random = config.random ?? Math.random;

  const shuffle = <T>(items: readonly T[]): T[] => {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = Math.floor(random() * (i + 1));
      const a = result[i];
      const b = result[j];
      if (a !== undefined && b !== undefined) {
        result[i] = b;
        result[j] = a;
      }
    }
    return result;
  };

  const makeRound = (): WhoSoundsRound<Id> => {
    const pool = shuffle(config.pool);
    const target = pool[0];
    if (!target) {
      throw new Error("WhoSounds: empty pool");
    }
    const options = shuffle(pool.slice(0, config.choices));
    return { target, options };
  };

  const state: WhoSoundsState = { round: 1, firstTryHits: 0, finished: false };
  let current = makeRound();
  let missedThisRound = false;

  return {
    state: () => ({ ...state }),
    currentRound: () => ({ target: current.target, options: [...current.options] }),
    choose: (id) => {
      if (state.finished) {
        return "finished";
      }
      if (id !== current.target.id) {
        missedThisRound = true;
        return "miss";
      }
      if (!missedThisRound) {
        state.firstTryHits += 1;
      }
      if (state.round >= config.rounds) {
        state.finished = true;
        return "finished";
      }
      state.round += 1;
      missedThisRound = false;
      current = makeRound();
      return "hit";
    },
  };
}
